import ActionButtonData from '../../schema/viewSchema/ActionButtonData';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import CardItem from '../../schema/viewSchema/CardItem';
import convertDateTimeToString from 'txp-data/lib/utils/view/convertDateTimeToString';
import createTxpDataRow from './createTxpDataRow';
import DetailsData from '../../schema/viewSchema/DetailsData';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import populateServiceReservationBusStopData from './populateServiceReservationBusStopData';
import Place from '../../schema/innerSchema/Place';
import ServiceReservation from '../../schema/ServiceReservation';
import TxpStrings from '../../schema/configSchema/TxpStrings';
import { RESERVATION_STATUS_CONFIRMED } from '../../helpers/ReservationStatusConstants';
import TxpDateTimeCallbacks from 'txp-data/lib/schema/configSchema/TxpDateTimeCallbacks';

// Extracts the event specific data needed for displaying the card
export default function createServiceReservationCard(
    serviceReservationEntity: ServiceReservation,
    isBusStop: boolean,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons,
    txpDateTimeCallbacks?: TxpDateTimeCallbacks
): CardItem {
    let busStopData: BusStopData;
    let locationText: string = '';
    let actionButtonData: ActionButtonData;

    // display the service reservation name
    let summaryFirstLine: string = serviceReservationEntity.name;

    //reservation code if available
    let summarySecondLine: string = serviceReservationEntity.reservationId
        ? txpStrings.formatters.format(
              txpStrings.strings.reservationCodeText,
              serviceReservationEntity.reservationId
          )
        : null;

    let startTime: string = convertDateTimeToString(
        serviceReservationEntity.startTimeFrom,
        false /* isAccessible */,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );

    let ariaStartTime: string = convertDateTimeToString(
        serviceReservationEntity.startTimeFrom,
        true,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );

    let locationInfo: Place = serviceReservationEntity.location;
    let telephone: string = '';

    if (locationInfo) {
        if (locationInfo.address) {
            locationText = locationInfo.address.streetAddress;
        }
        telephone = locationInfo.telephone;
    }

    /**
     * Determines whether card option should display "confirm" or "modify reservation"
     * based on reservation status. If reservation status is unknown, do not show option
     **/
    if (serviceReservationEntity.reservationStatus) {
        if (serviceReservationEntity.reservationStatus == RESERVATION_STATUS_CONFIRMED) {
            actionButtonData = serviceReservationEntity.modifyReservationUrl
                ? {
                      actionIconName: detailsIcons.edit,
                      actionUrl: serviceReservationEntity.modifyReservationUrl,
                      actionButtonText: txpStrings.strings.modifyReservationText,
                      actionName: ActionNameConstants.ModifyReservation,
                  }
                : null;
        } else {
            actionButtonData = serviceReservationEntity.confirmReservationUrl
                ? {
                      actionIconName: detailsIcons.accept,
                      actionUrl: serviceReservationEntity.confirmReservationUrl,
                      actionButtonText: txpStrings.strings.confirmReservationText,
                      actionName: ActionNameConstants.ConfirmReservation,
                  }
                : null;
        }
    }

    let detailsData: DetailsData = {
        firstLine: createTxpDataRow(
            startTime,
            txpStrings.strings.eventTimePrefixText,
            detailsIcons.clock
        ),

        secondLine: /** no end time */ null,

        thirdLine: telephone
            ? createTxpDataRow(
                  telephone,
                  txpStrings.strings.reservationPhoneText,
                  detailsIcons.phone
              )
            : null,

        fourthLine: showLocation
            ? createTxpDataRow(
                  locationText,
                  null,
                  detailsIcons.location,
                  false /* is required */,
                  txpStrings.strings.placeHolderForLocation,
                  true /* includeToolTip */
              )
            : null,
    };

    let ariaFormattedText: string = txpStrings.formatters.format(
        txpStrings.strings.eventAriaFormatText,
        ariaStartTime,
        detailsData.thirdLine && detailsData.thirdLine.text,
        null /** ticket number */,
        null /** seat details */,
        locationText
    );

    if (isBusStop) {
        busStopData = populateServiceReservationBusStopData(
            serviceReservationEntity,
            startTime,
            ariaStartTime,
            txpStrings,
            detailsIcons.appointment
        );
    }

    return {
        id: activityId,
        summaryData: {
            firstLine: createTxpDataRow(
                summaryFirstLine,
                null /* label */,
                null /* icon */,
                true /* isRequired */,
                txpStrings.strings.placeHolderForServiceAppointmentTitle
            ),
            secondLine: { text: summarySecondLine, icon: detailsIcons.contact },
        },
        entityIcon: entityIcon,
        entityImage: entityImage,
        activityType: ActivityType.ServiceAppointment,
        entityType: EntityType.ServiceReservation,
        detailsData: detailsData,
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        ariaText: ariaFormattedText,
        busStopData: busStopData,
        inferenceId: serviceReservationEntity.inferenceId,
        postalAddress: locationInfo && locationInfo.address,
        ariaTime: ariaStartTime,
    } as CardItem;
}
